import { useEffect, useMemo, useRef } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useConstituentStatus } from '#src/app/shared/constituents';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';
import { useIsMobile } from '#src/app/shared/hooks/userAgent';
import { getLocationLabel } from '#src/app/shared/utils/location';

type ConstituentStatusLike = Readonly<{
	isConstituent: boolean;
	method?: string | null;
}> | null;

type ExposurePetitionLike = Readonly<{
	relevantLocationLocalizedName: string | null;
	decisionMakersConnection: Readonly<{
		nodes: ReadonlyArray<Readonly<{ displayName?: string | null }>>;
		edges: ReadonlyArray<
			Readonly<{ associationData?: Readonly<{ constituentCountAmongSigners?: number | null }> | null }>
		>;
	}>;
}>;

type ExposureProperties = Readonly<{
	is_constituent: boolean;
	identification_method: string;
	has_decision_maker: boolean;
	saw_location_leaders: boolean;
	saw_constituent_threshold: boolean;
}>;

/**
 * Data-derived segmentation flags for the `$exposure` event (CHANGE-100773): the identification result,
 * whether the petition has a decision maker, whether the screens can name the place ("Nashville
 * leaders", from the same `getLocationLabel` the screens use) instead of the generic "decision makers",
 * and whether the constituent-count social proof is available to show. Kept out of the hook body so the
 * hook stays within the complexity budget.
 */
function resolveDmName(petition: ExposurePetitionLike): string | undefined {
	return petition.decisionMakersConnection.nodes.at(0)?.displayName || undefined;
}

function resolveConstituentCount(petition: ExposurePetitionLike): number {
	return petition.decisionMakersConnection.edges.at(0)?.associationData?.constituentCountAmongSigners ?? 0;
}

function resolveExposureProperties(status: ConstituentStatusLike, petition: ExposurePetitionLike): ExposureProperties {
	return {
		is_constituent: Boolean(status?.isConstituent),
		identification_method: status?.method ?? 'unknown',
		has_decision_maker: Boolean(resolveDmName(petition)),
		saw_location_leaders: Boolean(getLocationLabel(petition.relevantLocationLocalizedName)),
		saw_constituent_threshold: resolveConstituentCount(petition) > 1,
	};
}

/**
 * Constituents-in-PSF (CHANGE-100768): treats + fires `$exposure` the moment the share page renders for
 * an identified local constituent, so the treated population equals the exposed population equals the
 * signers who actually reach a variant surface. Control is treated + exposed too (it is a treatment arm
 * for lift analysis); only `v2`/`v4` render different screens, decided in `PageBody`.
 *
 * Eligibility is `isConstituent`, which is client-only state (sessionStorage, written at sign time) the
 * experiment service cannot target on, so it is a genuine relevance gate rather than a duplicate of the
 * config's targeting. `useConstituentStatus` resolves asynchronously (`null` on first render), so this
 * treats once the value resolves to an identified constituent; a `null` status (pending or
 * non-constituent) never treats.
 *
 * Mobile-web and US/en-US targeting live in the experiment config + access FC, not here, except the FCM
 * `enabled` re-check below: `treatExperiment` bypasses `enabledIf`, so the kill-switch has to be
 * re-checked at the call site, and `!fallback` keeps treated == exposed (the exposure hook refuses to
 * fire on a fallback resolution, so treating one would enroll a session that can never expose).
 */
export function useTreatConstituentPsfExperiment(): void {
	const {
		data: {
			petition,
			experiments: { constituentPsfExperiment: experiment },
		},
	} = usePsfSharePageData();
	const [constituentStatus] = useConstituentStatus(petition.id);
	const isMobile = useIsMobile();
	const utilityContext = useUtilityContext();
	const treatPromiseRef = useRef<Promise<void> | null>(null);

	const isEligible =
		Boolean(constituentStatus?.isConstituent) && isMobile && experiment.config.enabled && !experiment.fallback;

	// treatExperiment registers treatment server-side, but the resolved object stays treated:false, and
	// useTrackExperimentExposure no-ops on !treated — so the exposure object is armed with treated:true
	// from mount. Safe only because the sole call site below gates on treatExperiment's own return
	// (server-confirmed registration). Do not call trackExposure from anywhere else.
	const experimentForExposure = useMemo(() => ({ ...experiment, treated: true }), [experiment]);
	const trackExposure = useTrackExperimentExposure(experimentForExposure);

	// Funnel-segmentation properties stamped on $exposure so every arm (control included) can be sliced by
	// the conditions that drive the variant screens (CHANGE-100773). Derived from the data, not from what
	// rendered, so control carries the same flags for a like-for-like comparison.
	const exposureProperties = useMemo(
		() => resolveExposureProperties(constituentStatus, petition),
		[constituentStatus, petition],
	);

	useEffect(() => {
		if (!isEligible) return;
		// One-way latch holding the in-flight promise, never reset, so a re-render or a re-run of this
		// effect can't leak a second treat and double-count one supporter.
		treatPromiseRef.current ??= (async () => {
			// `treatExperiment` resolves `false` rather than throwing when the treatment did not land
			// server-side (network failure, bot session, not-yet-treatable assignment), so $exposure is gated
			// on that return: firing it for an unregistered treat would expose a session
			// experiment_variations has no record of.
			if (await treatExperiment(utilityContext, experiment.experimentName)) {
				await trackExposure(exposureProperties).catch(() => undefined);
			}
		})();
	}, [isEligible, experiment.experimentName, utilityContext, trackExposure, exposureProperties]);
}
